import React, { useState } from 'react';

import { Box, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

import { format } from 'date-fns';

import MultiSelect from 'components/common/Selects';
import { useRequestOptionsUnidades, useRequestOptionsIdentificacaoArrecadacao } from 'components/common/Selects/hooks';
import { BoxDivisor } from '../../../common/Formulario';
import { RelatorioMonthPicker, TODAY } from '../dateUtils';
import RelatorioToolbar from '../RelatorioToolbar';
import { CheckboxRelatorioContext } from '../checkbox/CheckboxRelatorioContextProvider';

const OpcoesImpressaoDemonstrativoCheckboxGrupo = ({
	handleChange,
	imprimirSubtotalReferencia,
	imprimirValorDoLancamento,
	imprimirTotalGeral,
	imprimirTotalUnidade,
	imprimirUnidadePorPagina,
	imprimirAssinatura,
}) => {
	return (
		<>
			<FormLabel component='legend'>Opções de Impressão</FormLabel>
			<FormGroup row fullWidth>
				<FormControlLabel
					control={
						<Checkbox
							checked={imprimirSubtotalReferencia}
							onChange={(e, checked) => handleChange(checked, 'imprimir_subtotal_by_referencia')}
							name='imprimir_subtotal_by_referencia'
						/>
					}
					label='Imprimir subtotal por referência'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={imprimirValorDoLancamento}
							onChange={(e, checked) => handleChange(checked, 'imprimir_valor_do_lancamento')}
							name='imprimir_valor_do_lancamento'
						/>
					}
					label='Imprimir valor do lançamento'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={imprimirTotalGeral}
							onChange={(e, checked) => handleChange(checked, 'imprimir_total_geral')}
							name='imprimir_total_geral'
						/>
					}
					label='Imprimir total geral'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={imprimirTotalUnidade}
							onChange={(e, checked) => handleChange(checked, 'imprimir_total_by_unidade')}
							name='imprimir_total_by_unidade'
						/>
					}
					label='Imprimir total por unidade'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={imprimirUnidadePorPagina}
							onChange={(e, checked) => handleChange(checked, 'imprimir_unidade_por_pagina')}
							name='imprimir_unidade_por_pagina'
						/>
					}
					label='Imprimir unidades por página'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={imprimirAssinatura}
							onChange={(e, checked) => handleChange(checked, 'imprimir_assinatura')}
							name='imprimir_assinatura'
						/>
					}
					label='Imprimir assinaturas'
				/>
			</FormGroup>
		</>
	);
};

const DemonstrativoArrecadacaoDetalhadoDivisor = ({ idCondominio, tipoRelatorio }) => {
	const [dataInicial, setDataInicial] = useState(TODAY);
	const [dataFinal, setDataFinal] = useState(TODAY);
	const [identificacao, setIdentificacao] = useState({
		id: 0,
		nome: 'Todas',
	});
	const {
		opcoesImpressao: {
			imprimir_subtotal_by_referencia,
			imprimir_valor_do_lancamento,
			imprimir_total_geral,
			imprimir_total_by_unidade,
			imprimir_unidade_por_pagina,
			imprimir_assinatura,
		},
		handleChangeOpcoesImpressao,
	} = React.useContext(CheckboxRelatorioContext);
	const [unidades, setUnidades] = useState([]);
	const { options: optionsUnidades } = useRequestOptionsUnidades();
	const { options: identificacoes } = useRequestOptionsIdentificacaoArrecadacao(idCondominio);

	React.useEffect(() => {
		if (unidades.length || identificacao) {
			setUnidades([]);
			setIdentificacao({
				id: 0,
				nome: 'Todas',
			});
		}
	}, [idCondominio]);

	const requestData = {
		condominio_id: idCondominio,
		mes_inicial: format(dataInicial, 'MM/yyyy'),
		mes_final: format(dataFinal, 'MM/yyyy'),
		identificacao_id: identificacao ? identificacao.id : '0',
		unidades_ids: unidades.map((v) => v.id),
		nome_relatorio: tipoRelatorio.action,
		imprimir_subtotal_by_referencia: imprimir_subtotal_by_referencia,
		imprimir_valor_do_lancamento: imprimir_valor_do_lancamento,
		imprimir_total_geral: imprimir_total_geral,
		imprimir_total_by_unidade: imprimir_total_by_unidade,
		imprimir_unidade_por_pagina: imprimir_unidade_por_pagina,
		imprimir_assinatura: imprimir_assinatura,
	};
	return (
		<BoxDivisor titulo='Demonstrativo de Arrecadação Detalhado'>
			<Box display='flex' mb='1em'>
				<Box flex={1} mr='1em'>
					<RelatorioMonthPicker
						label='Mês Inicial'
						handleChange={(newValue) => setDataInicial(newValue)}
						value={dataInicial}
						fullWidth
					/>
				</Box>
				<Box flex={1} mr='1em'>
					<RelatorioMonthPicker
						label='Mês Final'
						handleChange={(newValue) => setDataFinal(newValue)}
						value={dataFinal}
						fullWidth
					/>
				</Box>
				<Box flex={2}>
					<Autocomplete
						value={identificacao}
						onChange={(_, newValue) => setIdentificacao(newValue)}
						options={[...Object.values(identificacoes).filter((v) => v.id !== 0), { id: 0, nome: 'Todas' }]}
						getOptionLabel={(o) => o.nome}
						getOptionSelected={(option, value) => option.id === value.id && option.name === value.name}
						renderInput={(params) => (
							<TextField {...params} label='Identificação de Arrecadação' variant='outlined' />
						)}
						disabled={identificacoes.length === 1}
						clearText='Limpar'
						autoHighlight
						noOptionsText='Nenhuma identificação disponível'
						size='small'
						fullWidth
					/>
				</Box>
			</Box>
			<Box display='flex' mb='1em'>
				<Box width='50%' mr='1em'>
					<MultiSelect
						variant='formatsUnidades'
						options={optionsUnidades}
						value={unidades}
						setValue={setUnidades}
						size='small'
					/>
				</Box>
				<Box>
					<OpcoesImpressaoDemonstrativoCheckboxGrupo
						handleChange={handleChangeOpcoesImpressao}
						imprimirSubtotalReferencia={imprimir_subtotal_by_referencia}
						imprimirValorDoLancamento={imprimir_valor_do_lancamento}
						imprimirTotalGeral={imprimir_total_geral}
						imprimirTotalUnidade={imprimir_total_by_unidade}
						imprimirUnidadePorPagina={imprimir_unidade_por_pagina}
						imprimirAssinatura={imprimir_assinatura}
					/>
				</Box>
			</Box>
			<Box display='flex' justifyContent='flex-end'>
				<RelatorioToolbar data={requestData} tipoRelatorio={tipoRelatorio} valid={true} />
			</Box>
		</BoxDivisor>
	);
};

export default DemonstrativoArrecadacaoDetalhadoDivisor;
